"use client";

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@shared/components/ui/sidebar";
import {
  IconArrowLeft,
  IconBuilding,
  IconCoin,
  IconFlag,
  IconRouter,
  IconShield,
  IconTool,
  IconUsers,
} from "@tabler/icons-react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import type { ComponentProps } from "react";
import { Suspense } from "react";
import { NavUser } from "@/components/nav-user";
import { customerAppPath } from "@/lib/admin-env";
import { useAuth } from "@/lib/auth";

const adminNavItems = [
  { title: "Flags", url: "/flags", icon: IconFlag },
  { title: "Orgs", url: "/orgs", icon: IconBuilding },
  { title: "Credits", url: "/credits", icon: IconCoin },
  { title: "Users", url: "/users", icon: IconUsers },
  { title: "Admins", url: "/admins", icon: IconShield },
  { title: "Tools", url: "/tools", icon: IconTool },
  { title: "Load balancers", url: "/load-balancers", icon: IconRouter },
];

const AdminSidebarContent = ({ ...props }: ComponentProps<typeof Sidebar>) => {
  const { user, loading } = useAuth();
  const pathname = usePathname();

  const navUser = loading ? { name: "Loading…" } : user ? { name: user.name, email: user.email } : { name: "Sign in" };

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader className="ra-sidebar__header border-b border-border">
        <Link href="/" className="ra-sidebar__logo-link">
          <Image
            src="/brand/radium-logo-dark.svg"
            alt="Radium"
            width={120}
            height={24}
            className="ra-sidebar__logo-wordmark ra-sidebar__logo--on-light"
            aria-hidden
          />
          <Image
            src="/brand/radium-logo-light.svg"
            alt="Radium"
            width={120}
            height={24}
            className="ra-sidebar__logo-wordmark ra-sidebar__logo--on-dark"
            aria-hidden
          />
          <Image
            src="/brand/radium-icon-dark.svg"
            alt=""
            width={24}
            height={24}
            className="ra-sidebar__logo-icon ra-sidebar__logo--on-light"
            aria-hidden
          />
          <Image
            src="/brand/radium-icon-light.svg"
            alt=""
            width={24}
            height={24}
            className="ra-sidebar__logo-icon ra-sidebar__logo--on-dark"
            aria-hidden
          />
        </Link>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip="Back to app">
                  <a href={customerAppPath("/dashboard")}>
                    <IconArrowLeft className="ra-sidebar__menu-icon" aria-hidden />
                    <span>Back to app</span>
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel>Admin</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {adminNavItems.map((item) => {
                const isActive = pathname === item.url || pathname.startsWith(`${item.url}/`);
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild isActive={isActive} tooltip={item.title}>
                      <Link href={item.url}>
                        <item.icon className="ra-sidebar__menu-icon" aria-hidden />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="p-0 border-t border-border">
        <NavUser user={navUser} isAuthenticated={!!user} />
      </SidebarFooter>
    </Sidebar>
  );
};

export const AdminSidebar = ({ ...props }: ComponentProps<typeof Sidebar>) => {
  return (
    <Suspense fallback={null}>
      <AdminSidebarContent {...props} />
    </Suspense>
  );
};
